import { MonitorPlugin } from "./monitor-plugin";
import { listMonitorPlugins, type ListedMonitorPlugin } from "../../../components/forms/monitor-plugin-config";
import { showPluginMonitorForm } from "../../../components/forms/plugin-monitor-form";
import { DashboardWidgetService } from "../../dashboard/dashboard-widget-service";

// What Add Monitor lists: one entry per monitor plugin the filesets register, named and described by
// the plugin; its form is the generic one, or a kind's that claims the plugin.
export interface AddMonitorEntry {
  kind: string;
  title: string;
  description: string;
  openForm: (target?: unknown) => void;
}

// MonitorPluginService — the registry mapping a monitor kind to its MonitorPlugin. A direct
// generalization of DashboardWidgetService: a singleton over Maps, plugins self-register on import.
// Indexed two ways: by kind (for the catalog / picker) and by plugin name (to resolve a monitor row,
// by its plugin_name, to its kind). Registering a plugin also registers its dashboard widget into
// DashboardWidgetService, so a kind is declared in ONE place.
export class MonitorPluginService {
  static readonly _instance: MonitorPluginService = new MonitorPluginService();

  static get(): MonitorPluginService {
    return this._instance;
  }

  private byKind = new Map<string, MonitorPlugin>();
  private byCheckpointPlugin = new Map<string, MonitorPlugin>();
  // what a monitor that points at a checkpoint plugin no registered kind claims resolves to: the generic
  // plugin-monitor kind, whose form is generated from the plugin's configuration type
  private fallback: ((row: MonitorKindRow, pluginName: string) => MonitorPlugin | undefined) | undefined;

  registerFallback(f: (row: MonitorKindRow, pluginName: string) => MonitorPlugin | undefined): void {
    this.fallback = f;
  }
  // The monitor plugins the active filesets register, by plugin name, for the generic kind: a plugin's
  // kind is its readings' category and its `signals` literal says which readings are its chips. Loaded
  // once per page by ensureRegistrations (the monitors list awaits it before it fetches readings).
  private registrations: Map<string, ListedMonitorPlugin> | undefined;
  private loading: Promise<void> | undefined;
  setRegistrations(plugins: ListedMonitorPlugin[]): void {
    this.registrations = new Map(plugins.map((p) => [p.name, p]));
  }
  registration(pluginName: string): ListedMonitorPlugin | undefined {
    return this.registrations?.get(pluginName);
  }
  ensureRegistrations(): Promise<void> {
    if (this.registrations) return Promise.resolve();
    this.loading ??= listMonitorPlugins()
      .then((plugins) => this.setRegistrations(plugins))
      .catch((e: unknown) => console.warn("monitor plugins | the registrations could not be read; plugin monitors show no chips:", e))
      .finally(() => (this.loading = undefined));
    return this.loading;
  }

  register(p: MonitorPlugin): void {
    this.byKind.set(p.kind, p);
    if (p.checkpointPlugin) this.byCheckpointPlugin.set(p.checkpointPlugin, p);
    if (p.widget) DashboardWidgetService.get().register(p.widget); // one registration, two registries
  }

  byKindKey(kind: string): MonitorPlugin | undefined {
    return this.byKind.get(kind);
  }

  // Resolve a monitor row to its kind by the plugin it names (plugin_name); a plugin no registered kind
  // claims goes to the generic plugin-monitor kind. Returns undefined for a row naming none — the caller
  // renders the neutral default (empty signal cell / no charts).
  forMonitor(row: MonitorKindRow): MonitorPlugin | undefined {
    const name = row.plugin_name ?? "";
    if (!name) return undefined;
    return this.byCheckpointPlugin.get(name) ?? this.fallback?.(row, name);
  }

  list(): MonitorPlugin[] {
    return [...this.byKind.values()];
  }
  // Add Monitor's list: every registered plugin that collects, or that a kind still makes monitors for.
  // From the registrations, which ensureRegistrations loads; empty until they are.
  addMonitorEntries(): AddMonitorEntry[] {
    void this.ensureRegistrations();
    const out: AddMonitorEntry[] = [];
    for (const p of this.registrations?.values() ?? []) {
      const kind = this.byCheckpointPlugin.get(p.name);
      if (!p.collects && !kind?.createsMonitors) continue;
      out.push({
        kind: kind?.kind ?? `plugin:${p.name}`,
        title: p.title || p.name,
        description: p.description || kind?.description || "",
        openForm: kind?.openForm ?? (() => showPluginMonitorForm({ filesetUid: p.filesetUid, fileset: p.fileset, plugin: p.name })),
      });
    }
    return out.sort((a, b) => a.title.localeCompare(b.title));
  }
}

// MonitorKindRow is what resolving a monitor's kind reads off its row.
export interface MonitorKindRow {
  plugin_name?: string | null;
  plugin_fileset_uid?: string | null;
  extra_data?: unknown; // object or json text
}
